import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { useBusinessWorkspace, workspacePath, businessWorkPath } from './BusinessWorkspaceContext';
import { requestKey } from './businessRequestKey';

// Each renderer mounts this session under its account, company and conversation key.
export function useBusinessChat(conversationId) {
  const context = useBusinessWorkspace(), router = useRouter();
  const { api, workspaceId } = context;
  const [data, setData] = useState(null), [loading, setLoading] = useState(true), [error, setError] = useState(null);
  const [content, setContent] = useState(''), [modelId, setModelId] = useState(''), [stream, setStream] = useState('');
  const [busy, setBusy] = useState(false), [retryIntent, setRetryIntent] = useState(null), [action, setAction] = useState(null), [title, setTitle] = useState('');
  const alive = useRef(true), busyRef = useRef(false), generation = useRef(0), controller = useRef(new AbortController());
  const accessError = useRef(null);
  const current = () => alive.current && context.current();
  const options = () => ({ isRequestCurrent: current, signal: controller.current.signal });
  const load = async ({ quiet = false } = {}) => {
    const ticket = ++generation.current;
    if (!quiet) setLoading(true);
    try {
      const [workspace, balance, catalog, chat, runs] = await Promise.all([
        api.get(workspaceId, options()), api.balance(workspaceId, options()), api.chatModels(workspaceId, options()),
        conversationId ? api.chat(workspaceId, conversationId, options()) : null,
        conversationId ? api.chatRuns(workspaceId, conversationId, options()) : { items: [] },
      ]);
      if (!current() || generation.current !== ticket) return;
      if (workspace.id !== workspaceId || chat && (chat.workspaceId !== workspaceId || String(chat.id) !== conversationId)) throw new Error('This chat is unavailable in this workspace.');
      setData({ workspace, balance, catalog, chat, runs: runs.items });
      if (chat?.coworkerSession) router.replace(businessWorkPath(workspaceId, chat.coworkerSession));
      accessError.current = null;
      setModelId(previous => catalog.models.some(model => model.id === previous) ? previous : catalog.models[0]?.id || '');
    } catch (error) {
      if (current() && generation.current === ticket) {
        setError(error.message);
        // Do not retain private content after a failed authorization refresh.
        if ([401, 403, 404].includes(error.status)) { accessError.current = error; setData(null); setStream(''); setContent(''); setRetryIntent(null); }
      }
    } finally { if (current() && generation.current === ticket) setLoading(false); }
  };
  useEffect(() => {
    alive.current = true; controller.current = new AbortController(); void load();
    return () => { alive.current = false; generation.current++; controller.current.abort(); };
  }, []);
  const lastRun = data?.runs[0], active = data?.runs.some(run => run.active);
  const processing = data?.runs.some(run => run.active && ['held', 'dispatched', 'received'].includes(run.state));
  useEffect(() => {
    if (!processing || busy) return;
    // Poll only a run the server confirms is still active. Unknown usage uses
    // explicit reconciliation and the server's durable recovery worker.
    const timer = setInterval(() => { void load({ quiet: true }); }, 3000);
    return () => clearInterval(timer);
  }, [processing, busy]);
  const execute = async operation => {
    if (busyRef.current || !current()) return;
    busyRef.current = true; setBusy(true); setError(null);
    try { await operation(); }
    catch (error) { if (current()) setError(accessError.current?.message || error.message); }
    finally { if (current()) { busyRef.current = false; setBusy(false); } }
  };
  const send = event => {
    event?.preventDefault?.();
    void execute(async () => {
      const model = data.catalog.models.find(item => item.id === modelId);
      if (!retryIntent && (!content.trim() || !model)) return;
      const intent = retryIntent || { key: requestKey(), body: { content, modelId, maxOutputTokens: model.maxOutputTokens } };
      setStream(''); setRetryIntent(intent);
      try {
        const result = await api.sendChat(workspaceId, conversationId, intent.body, intent.key, {
          ...options(), onDelta: value => { if (current()) setStream(text => text + value); },
        });
        if (!current()) return;
        setContent(''); setRetryIntent(null);
        if (result.state === 'failed') setError('The model could not complete this reply. You can send a new message.');
      } catch (error) {
        if (current() && error.status >= 400 && error.status < 500) setRetryIntent(null);
        throw error;
      } finally {
        if (current()) { await load({ quiet: true }); if (current()) { setStream(''); context.changed(); } }
      }
    });
  };
  const reconcile = () => execute(async () => {
    await api.reconcileChat(workspaceId, lastRun.id, options());
    if (current()) { await load({ quiet: true }); if (current()) context.changed(); }
  });
  const change = () => execute(async () => {
    if (action === 'delete') {
      await api.deleteChat(workspaceId, conversationId, options());
      if (current()) { context.changed(); router.replace(workspacePath(workspaceId)); }
      return;
    }
    await api.updateChat(workspaceId, conversationId, action === 'rename' ? { title } : { archived: !data.chat.flags?.isArchived }, options());
    if (current()) { setAction(null); await load({ quiet: true }); if (current()) context.changed(); }
  });

  return { data, loading, error, content, setContent, modelId, setModelId, stream, busy, retryIntent, action, setAction, title, setTitle, lastRun, active, processing, load, send, reconcile, change };
}
